import { Capacitor } from '@capacitor/core';
import { BehaviorSubject } from 'rxjs';
import { SendIntent } from 'send-intent';

/**
 * Utility function to dynamically import Filesystem and Directory
 * This ensures that all imports are consistent throughout the codebase
 * and prevents Vite from both statically and dynamically importing the same module
 */
const getFilesystem = async () => {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  return { Filesystem, Directory };
};

// Define the Intent interface to match the send-intent plugin's structure
interface Intent {
  files?: Array<{
    path: string;
    type: string;
  }>;
  text?: string;
  extras?: Record<string, any>;
  // Additional properties that might be present in iOS share extension
  url?: string;
  type?: string;
  title?: string;
  description?: string;
}

interface SharedMedia {
  type: string; // 'image', 'video', 'text', 'url', 'file'
  path?: string;
  url?: string;
  data?: string;
  text?: string;
  mimeType?: string;
  value?: string;
  fileContent?: string;
}

class ShareIntentService {
  private sharedMediaSubject = new BehaviorSubject<SharedMedia[]>([]);
  public sharedMedia$ = this.sharedMediaSubject.asObservable();

  constructor() {
    this.initialize();
  }

  private async initialize() {
    try {
      // Check if we have a pending intent when the app starts
      this.checkForSharedContent();

      // Listen for the sendIntentReceived event from the plugin
      // This is the key event that the send-intent plugin dispatches
      window.addEventListener('sendIntentReceived', () => {
        //alert('🔴 sendIntentReceived event received! 50');
        this.checkForSharedContent();
      });

    } catch (error) {
      console.error('Error initializing ShareIntentService:', error);
    }
  }

  private async checkForSharedContent() {
    try {

      // First check if there's anything in the native ShareStore
      // This is where the AppDelegate stores shared items

      // For iOS, we need to check both the intent and the native layer
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        // First check if there's a direct file URL that was opened
        // This will be from the AppDelegate handling file:// URLs
        console.log('Checking for shared content...');
        try {
          const intent = await SendIntent.checkSendIntentReceived() as Intent;
          console.log('Intent received:', intent);

          // Check if we have a file URL directly in the intent
          if (intent && intent.url) {
            // Create a file object from the URL
            const file = {
              path: intent.url,
              type: intent.type || this.getMimeTypeFromPath(intent.url)
            };
            this.handleSharedFiles([file]);
            return;
          }

          // Check for files array
          if (intent && intent.files && intent.files.length > 0) {
            alert('Found shared files from intent: ' + JSON.stringify(intent.files));
            this.handleSharedFiles(intent.files);
            return;
          }

          // Check if we have title and url properties (from AppDelegate)
          if (intent && intent.title && intent.url) {
            alert('Found shared item with title and URL: ' + intent.title);
            const file = {
              path: intent.url,
              type: intent.type || this.getMimeTypeFromPath(intent.url)
            };
            this.handleSharedFiles([file]);
            return;
          }
        } catch (intentError) {
          // console.log('No intent files found, this is normal:', intentError);
          // This is expected if no intent was shared, continue to check Documents directory
        }

        // If no intent files, check for any files in the app's Documents directory
        // that might have been copied there by the AppDelegate
        try {
          // Get Filesystem and Directory consistently
          const { Filesystem, Directory } = await getFilesystem();
          const result = await Filesystem.readdir({
            path: '/',
            directory: Directory.Documents
          });

          if (result && result.files && result.files.length > 0) {
            // Look for recently added files (shared files)
            const recentFiles = result.files
              .filter(file =>
                file.name.startsWith('shared_') ||
                file.name.includes('.jpg') ||
                file.name.includes('.jpeg') ||
                file.name.includes('.png') ||
                file.name.includes('.mp4') ||
                file.name.includes('.mov') ||
                file.name.includes('.heic')
              )
              .sort((a, b) => b.mtime - a.mtime) // Sort by modification time, newest first
              .slice(0, 8); // Take up to 5 most recent files

            if (recentFiles.length > 0) {
              alert(`Found ${recentFiles.length} recent files in Documents directory`);

              // Convert to the format expected by handleSharedFiles
              const files = recentFiles.map(file => {
                const path = `${Directory.Documents}/${file.name}`;
                const type = this.getMimeTypeFromPath(file.name);
                return { path, type };
              });

              this.handleSharedFiles(files);
              return;
            }
          }
        } catch (dirError) {
          console.log('Error reading Documents directory, this may be normal:', dirError);
          // Continue execution, this might be normal
        }
      } else {
        // For Android, check the intent
        try {
          const intent = await SendIntent.checkSendIntentReceived() as Intent;

          if (intent && intent.files && intent.files.length > 0) {
            this.handleSharedFiles(intent.files);
            return;
          }
        } catch (androidError) {
          console.log('Error checking Android intent, this may be normal:', androidError);
          // Continue execution, this might be normal
        }
      }
    } catch (error) {
      console.error('Error checking for shared content:', error);
      // Don't show alert for general errors, just log them
    }
  }

  private async handleSharedFiles(files: Array<{ path: string; type: string }>) {
    try {
      const processedFiles = await Promise.all(files.map(async (file) => {
        // Determine if this is an image or video based on the file type
        const isImage = file.type?.includes('image') ||
          file.path?.toLowerCase().endsWith('.jpg') ||
          file.path?.toLowerCase().endsWith('.jpeg') ||
          file.path?.toLowerCase().endsWith('.png') ||
          file.path?.toLowerCase().endsWith('.heic');

        const isVideo = file.type?.includes('video') ||
          file.path?.toLowerCase().endsWith('.mp4') ||
          file.path?.toLowerCase().endsWith('.mov') ||
          file.path?.toLowerCase().endsWith('.avi');

        // Check if it's a web URL
        const isWebUrl = file.path.startsWith('http://') || file.path.startsWith('https://');
        if (isWebUrl) {
          return {
            type: 'url',
            path: file.path,
            value: file.path,
            mimeType: file.type
          };
        }

        const mediaType = isVideo ? 'video' : (isImage ? 'image' : 'file');

        const result: SharedMedia = {
          type: mediaType,
          path: file.path,
          mimeType: file.type || this.getMimeTypeFromPath(file.path),
          value: file.path
        };

        // Try to read the file content if it has a URL
        if (file.path) {
          try {
            let filePath = decodeURIComponent(file.path);

            // Special handling for iOS file URLs
            if (Capacitor.getPlatform() === 'ios') {
              // On iOS, we need to handle file:// URLs specially
              let fileContent = undefined;

              if (filePath.startsWith('file://')) {
                try {
                  // Get Filesystem consistently
                  const { Filesystem } = await getFilesystem();
                  const content = await Filesystem.readFile({
                    path: filePath
                  });
                  fileContent = content.data;
                } catch (err1) {
                  console.error('Error reading file:// URL:', err1);
                }
              }

              if (fileContent) {
                result.fileContent = fileContent as string;
              } else {
                // If we can't read the file content, at least try to get the file URL working
                // This will allow us to display the image/video even if we can't read the content
                alert('Could not read file content with any method, using file URL directly');

                // For iOS, if the file is in the Documents directory, make sure the path is correct
                if (filePath.includes('/Documents/')) {
                  const filename = filePath.split('/').pop();
                  if (filename) {
                    // Get Directory consistently
                    const { Directory } = await getFilesystem();
                    result.path = `file://${Directory.Documents}/${filename}`;
                    result.value = result.path;
                  }
                }
              }
            } else {
              // Standard file handling for other platforms
              // Remove file:// prefix if present
              if (filePath.startsWith('file://')) {
                filePath = filePath.substring(7);
              }

              console.log(`Reading file from path: ${filePath}`);
              try {
                // Get Filesystem consistently
                const { Filesystem } = await getFilesystem();
                const content = await Filesystem.readFile({
                  path: filePath
                });

                console.log('File content read successfully');
                result.fileContent = content.data as string;
              } catch (readErr) {
                console.error('Error reading file content:', readErr);
              }
            }
          } catch (err) {
            console.log(`Error reading file: ${err}`);
            console.error('Error reading file:', err);
            // Continue with the file path even if reading fails
          }
        }

        return result;
      }));
      this.sharedMediaSubject.next(processedFiles);
    } catch (error) {
      console.error('Error processing shared files:', error);
    }
  }

  // Method to clear shared media after it's been processed
  public clearSharedMedia() {
    this.sharedMediaSubject.next([]);

    // Also clear the intent in the plugin if possible
    try {
      // This is not a standard method, but some implementations might support it
      (SendIntent as any).reset?.();
    } catch (error) {
      throw new Error('Error clearing shared intent: ' + error);
    }
  }

  // Helper method to determine MIME type from file path
  private getMimeTypeFromPath(path: string): string {
    if (!path) return 'application/octet-stream';

    const extension = path.split('.').pop()?.toLowerCase() || '';
    switch (extension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'heic':
        return 'image/heic';
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      case 'pdf':
        return 'application/pdf';
      default:
        return 'application/octet-stream';
    }
  }
}

export const shareIntentService = new ShareIntentService();
